import { FC } from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { ProductType } from '@tink/core/src/types';
import { GradualReleaseLabels } from '../../utilities/useGradualRolloutTypes';
import {
  ProductProviderSelection,
  ProductProviderSelectionWithRollout,
} from '../../core/ProviderSelection/ProductProviderSelection';
import { Version } from '../../models/version';
import { AppInitiator } from '../AppInitiator/AppInitiator';
import { VersionProvider } from '../ApplicationProviders/VersionProvider/VersionProvider';
import { LazyRefreshUserCredentials } from '../screens/RefreshUserCredentials/LazyRefreshUserCredentials';
import { LazyAuthenticateUserCredentials } from '../screens/AuthenticateUserCredentials/LazyAuthenticateUserCredentials';
import { LazyPaymentPermanentDirector } from '../../payments/legacy/components/PermanentUserPaymentDirector/LazyPaymentPermanentDirector';
import { ProductProvider } from '../ApplicationProviders/ProductProvider/ProductProvider';
import { BaseRoutes } from '../../core/BaseRoutes/BaseRoutes';
import { UserInitiator } from '../UserInitiator/UserInitiator';
import {
  ProductDimension,
  ProductDimensionAccountCheck,
  ProductDimensionBusinessAccountCheck,
  ProductDimensionExpenseCheck,
  ProductDimensionIncomeCheck,
  ProductDimensionPay,
  ProductDimensionProducts,
  ProductDimensionReports,
  ProductDimensionRiskInsights,
  ProductDimensionTransactions,
} from '../../models/product';
import { CredentialsInitiator } from '../../core/CredentialsInitiator/CredentialsInitiator';
import { LazyUpdateConsentLanding } from '../UpdateConsentLanding/LazyUpdateConsentLanding';
import { LazyCredentialsDirector } from '../CredentialsDirector/LazyCredentialsDirector';
import { ProviderInitiator } from '../ProviderInitiator/ProviderInitiator';
import { LazyVrpMandateMachineView } from '../../payments/VRP/components/VrpMandateDirector/LazyVrpMandateMachineView';
import { extendConsentProductsRegex } from '../../extend-consent/components/ExtendConsentDirector';
import { ExtendConsentLanding } from '../../extend-consent/components/ExtendConsentLanding';
import { ClientProvider } from '../ApplicationProviders/ClientProvider/ClientProvider';
import { ConsentDirector } from '../../connectivity/screens/consent/ConsentDirector';
import { HandoffCredentialsFinder } from '../HandoffCredentialsFinder/HandoffCredentialsFinder';
import { LazyHandoffBankIdDevice } from '../screens/HandoffBankIdDevice/LazyHandoffBankIdDevice';
import { HandoffCredentialsAction } from '../../models/handoffSession';
import {
  LazyBundledFlowDeviceHandoff,
  LazyDeviceHandoff,
} from '../screens/DeviceHandoff/LazyDeviceHandoff';
import { DeviceHandoffLanding } from '../../payments/legacy/components/DeviceHandoffLanding/DeviceHandoffLanding';
import { LazyHandoffCompleted } from '../screens/HandoffCompleted/LazyHandoffCompleted';
import { HandoffConsumerMachineView } from '../../connectivity/components/HandoffConsumerMachineView';
import { DemoBankScenarioProvider } from '../ApplicationProviders/DemoBankScenarioProvider/DemoBankScenarioProvider';
import { ProviderContainer } from './ProviderContainer/ProviderContainer';

type ProductRouterMatchProps = RouteComponentProps<{
  version: string;
  productType: ProductType;
  productDimension: ProductDimension;
}>;

export const ProductRouter: FC<ProductRouterMatchProps> = ({
  match: {
    params: { version, productType, productDimension },
  },
}) => (
  <VersionProvider initialVersion={new Version(version)}>
    <ProductProvider initialProduct={{ productType, productDimension }}>
      <UserInitiator>
        <CredentialsInitiator>
          <ProviderInitiator productType={productType} productDimension={productDimension}>
            <AppInitiator productType={productType} productDimension={productDimension}>
              <DemoBankScenarioProvider>
                <ClientProvider>
                  <Switch>
                    <Route
                      path="/:version/:productType/:productDimension/mobile-handoff/:credentialsAction"
                      component={HandoffConsumerMachineView}
                    />
                    <Route path="/:version/:productType/:productDimension/handoff-mobile-device/bankid-auth">
                      <HandoffCredentialsFinder>
                        <LazyHandoffBankIdDevice />
                      </HandoffCredentialsFinder>
                    </Route>

                    <Route
                      path={`/:version/${ProductType.REPORTS}/${ProductDimensionReports.CREATE_REPORT}/handoff-mobile-device/${HandoffCredentialsAction.ADD_CREDENTIALS}`}
                      component={LazyBundledFlowDeviceHandoff}
                    />

                    <Route
                      path={`/:version/${ProductType.BUSINESS_ACCOUNT_CHECK}/${ProductDimensionBusinessAccountCheck.CREATE_REPORT}/handoff-mobile-device/${HandoffCredentialsAction.ADD_CREDENTIALS}`}
                      component={LazyBundledFlowDeviceHandoff}
                    />
                    <Route
                      path="/:version/:productType/:productDimension/handoff-mobile-device/:credentialsAction"
                      component={LazyDeviceHandoff}
                    />

                    <Route
                      path="/:version/pay/:productDimension/handoff-mobile-device"
                      component={DeviceHandoffLanding}
                    />
                    <Route
                      path="/:version/:productType/:productDimension/handoff-completed"
                      component={LazyHandoffCompleted}
                    />

                    <Route
                      exact
                      path={[
                        '/:version/:productType/:productDimension/:providerName',
                        '/:version/:productType/:productDimension/:providerName/fts',
                        '/:version/:productType/:productDimension/:providerName/with-source-account',
                        '/:version/:productType/:productDimension/:providerName/handoff-mobile-device',
                        '/:version/:productType/:productDimension/:providerName/handoff-mobile-device-with-source-account',
                      ]}
                      component={ProviderContainer}
                    />

                    <Route
                      path="/:version/pay/credentials"
                      component={LazyPaymentPermanentDirector}
                    />

                    <Route
                      exact
                      path={`/:version/${ProductType.PAY}/${ProductDimensionPay.VRP_MANDATE}`}
                      component={LazyVrpMandateMachineView}
                    />
                    <Route
                      exact
                      path={`/:version/${ProductType.PAY}/${ProductDimensionPay.VRP_MANDATE_PICKUP}`}
                    >
                      <LazyVrpMandateMachineView initialState="getConsentAfterPickup" />
                    </Route>

                    <Route
                      path={[
                        '/:version/transactions/update-consent',
                        '/:version/products/update-consent',
                        '/:version/account-check/update-consent',
                      ]}
                      component={LazyUpdateConsentLanding}
                    />

                    <Route
                      path={`/:version/:productType(${extendConsentProductsRegex})/extend-consent`}
                      component={ExtendConsentLanding}
                    />

                    <Route path="/:version/credentials/refresh">
                      <LazyCredentialsDirector>
                        <LazyRefreshUserCredentials />
                      </LazyCredentialsDirector>
                    </Route>

                    <Route path="/:version/credentials/authenticate">
                      <LazyCredentialsDirector>
                        <LazyAuthenticateUserCredentials />
                      </LazyCredentialsDirector>
                    </Route>

                    <Route
                      path={`/1.0/${ProductType.ACCOUNT_CHECK}/${ProductDimensionAccountCheck.CREATE_REPORT}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.ACCOUNT_CHECK}
                          productDimension={ProductDimensionAccountCheck.CREATE_REPORT}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      path={`/1.0/${ProductType.INCOME_CHECK}/${ProductDimensionIncomeCheck.CREATE_REPORT}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.INCOME_CHECK}
                          productDimension={ProductDimensionIncomeCheck.CREATE_REPORT}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      exact
                      path={`/1.0/${ProductType.EXPENSE_CHECK}/${ProductDimensionExpenseCheck.CREATE_REPORT}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.EXPENSE_CHECK}
                          productDimension={ProductDimensionExpenseCheck.CREATE_REPORT}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      exact
                      path={`/1.0/${ProductType.RISK_INSIGHTS}/${ProductDimensionRiskInsights.CREATE_REPORT}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.RISK_INSIGHTS}
                          productDimension={ProductDimensionRiskInsights.CREATE_REPORT}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      exact
                      path={`/1.0/${ProductType.BUSINESS_ACCOUNT_CHECK}/${ProductDimensionBusinessAccountCheck.CREATE_REPORT}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelectionWithRollout
                          version="1.0"
                          productType={ProductType.BUSINESS_ACCOUNT_CHECK}
                          productDimension={ProductDimensionBusinessAccountCheck.CREATE_REPORT}
                          gradualReleaseLabel={GradualReleaseLabels.BAC_PROVIDERS}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      exact
                      path={`/1.0/${ProductType.REPORTS}/${ProductDimensionReports.CREATE_REPORT}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.REPORTS}
                          productDimension={ProductDimensionReports.CREATE_REPORT}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      exact
                      path={`/1.0/${ProductType.PRODUCTS}/${ProductDimensionProducts.CONNECT_ACCOUNTS}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.PRODUCTS}
                          productDimension={ProductDimensionProducts.CONNECT_ACCOUNTS}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      path={[
                        `/1.0/${ProductType.TRANSACTIONS}/${ProductDimensionTransactions.CONNECT_ACCOUNTS}`,
                        `/1.0/${ProductType.TRANSACTIONS}/${ProductDimensionTransactions.ADD_ACCOUNTS}`,
                      ]}
                    >
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.TRANSACTIONS}
                          productDimension={ProductDimensionTransactions.CONNECT_ACCOUNTS}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route
                      exact
                      path={`/1.0/${ProductType.BUSINESS_TRANSACTIONS}/${ProductDimensionTransactions.CONNECT_ACCOUNTS}`}
                    >
                      <ConsentDirector>
                        <ProductProviderSelectionWithRollout
                          version="1.0"
                          productType={ProductType.BUSINESS_TRANSACTIONS}
                          productDimension={ProductDimensionTransactions.CONNECT_ACCOUNTS}
                          gradualReleaseLabel={GradualReleaseLabels.BTRX_PROVIDERS}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route exact path={`/1.0/${ProductType.PAY}/${ProductDimensionPay.DIRECT}`}>
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.PAY}
                          productDimension={ProductDimensionPay.DIRECT}
                        />
                      </ConsentDirector>
                    </Route>

                    <Route exact path={`/1.0/${ProductType.PAY}/${ProductDimensionPay.BULK}`}>
                      <ConsentDirector>
                        <ProductProviderSelection
                          version="1.0"
                          productType={ProductType.PAY}
                          productDimension={ProductDimensionPay.BULK}
                        />
                      </ConsentDirector>
                    </Route>

                    <ConsentDirector>
                      <BaseRoutes />
                    </ConsentDirector>
                  </Switch>
                </ClientProvider>
              </DemoBankScenarioProvider>
            </AppInitiator>
          </ProviderInitiator>
        </CredentialsInitiator>
      </UserInitiator>
    </ProductProvider>
  </VersionProvider>
);
